import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataContext } from "../../context/DataContext";
import { ApiContext } from "../../Root";
import { useAuth } from "../../authentication/AuthContextProvider";

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
});

type TicketForm = {
  title: string;
  orderNumber: string;
  phoneNumber: string;
  email: string;
  category: string;
  componentName: string;
  faultSymptom: string;
  replacementMaterial: boolean;
};

const emptyForm: TicketForm = {
  title: "",
  orderNumber: "",
  phoneNumber: "",
  email: "",
  category: "",
  componentName: "",
  faultSymptom: "",
  replacementMaterial: false,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const CreateTicketModal = ({
  open,
  onOpenChange,
}: Props): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dataContext = useContext(DataContext);
  const apiContext = useContext(ApiContext);
  const auth = useAuth();
  const [form, setForm] = useState<TicketForm>(emptyForm);
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid =
    form.title.trim() !== "" &&
    form.orderNumber.trim() !== "" &&
    form.phoneNumber.trim() !== "" &&
    form.email.trim() !== "" &&
    form.category !== "" &&
    form.componentName.trim() !== "" &&
    form.faultSymptom.trim() !== "";

  const handleClose = () => {
    onOpenChange(false);
    setForm(emptyForm);
    setImage(null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await apiContext.customerOrderApi.createTicket(dataContext, auth.user, dataContext.state.selectedCustomer, form, image, t);
    await apiContext.customerOrderApi.getTickets(dataContext, auth.user, dataContext.state.selectedCustomer, t);
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("buttons.createTicket")}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t("labels.title")} required>
                <Input
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                />
              </Field>
              <Field label={t("labels.orderNumber")} required>
                <Input
                  value={form.orderNumber}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, orderNumber: e.target.value }))
                  }
                />
              </Field>
              <Field label={t("labels.phoneNumber")} required>
                <Input
                  type="tel"
                  value={form.phoneNumber}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, phoneNumber: e.target.value }))
                  }
                />
              </Field>
              <Field label={t("labels.email")} required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, email: e.target.value }))
                  }
                />
              </Field>
              <Field label={t("labels.category")} required>
                <Select
                  value={form.category}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, category: e.target.value }))
                  }
                  select={{
                    style: { color: form.category === "" ? tokens.colorNeutralForeground3 : "inherit" },
                  }}
                >
                  <option value="" disabled>{t("labels.categoryPlaceholder")}</option>
                  <option value="Elektronik">{t("labels.electronics")}</option>
                  <option value="Mekanik">{t("labels.mechanics")}</option>
                  <option value="Material i leverans">
                    {t("labels.materialInDelivery")}
                  </option>
                </Select>
              </Field>
              <Field
                label={t("labels.componentName")}
                required
              >
                <Input
                  value={form.componentName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, componentName: e.target.value }))
                  }
                />
              </Field>
              <Field label={t("labels.faultSymptom")} required>
                <Textarea
                  value={form.faultSymptom}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, faultSymptom: e.target.value }))
                  }
                  resize="vertical"
                />
              </Field>
              <Field hint={t("labels.replacementMaterialHint")}>
                <Checkbox
                  checked={form.replacementMaterial}
                  onChange={(_, data) =>
                    setForm((p) => ({
                      ...p,
                      replacementMaterial: !!data.checked,
                    }))
                  }
                  label={t("labels.replacementMaterialWanted")}
                />
              </Field>
              <Field label={t("labels.image")}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files?.[0] ?? null)}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isSubmitting} onClick={handleClose}>
               {t("buttons.cancel")}
            </Button>
            <Button
              appearance="primary"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit}
              icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
            >
              {t("buttons.create")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
